import { useEffect, useState } from 'react';
import apiClient from '../../utils/api';
import { formatUsd } from '../../data/countries';

const statusLabel = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'draft') return 'Draft';
  if (s === 'approved') return 'Approved';
  if (s === 'processing') return 'Processing';
  if (s === 'completed') return 'Completed';
  return status || '—';
};

const AdminDistributionPanel = ({ catalogProperties = [], addToast }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [form, setForm] = useState({
    propertyId: '',
    periodKey: '',
    grossUsd: '',
    note: '',
  });

  const loadRuns = async () => {
    setLoading(true);
    try {
      const res = await apiClient.listDistributionRuns();
      setRuns(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      addToast(err.message || 'Failed to load distribution runs', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRuns();
  }, []);

  const saleProperties = (catalogProperties || []).filter((p) => p?.id);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await apiClient.createDistributionRun({
        propertyId: form.propertyId,
        periodKey: form.periodKey.trim(),
        grossUsd: Number(form.grossUsd),
        note: form.note,
      });
      addToast(`Draft created for ${res.data?.totalRecipients || 0} shareholders`, 'success');
      setForm({ propertyId: form.propertyId, periodKey: '', grossUsd: '', note: '' });
      loadRuns();
    } catch (err) {
      addToast(err.message || 'Failed to create distribution run', 'error');
    }
  };

  const handleApprove = async (runId) => {
    try {
      await apiClient.approveDistributionRun(runId);
      addToast('Distribution run approved', 'success');
      loadRuns();
    } catch (err) {
      addToast(err.message || 'Approve failed', 'error');
    }
  };

  const handleProcess = async (runId) => {
    setProcessingId(runId);
    try {
      let completed = false;
      do {
        const res = await apiClient.processDistributionRun(runId, { limit: 250 });
        completed = Boolean(res.data?.completed);
        if (res.data?.batchPaid) {
          addToast(`Paid ${res.data.batchPaid} recipient(s) in batch`, 'success');
        }
      } while (!completed);
      addToast('Distribution run completed', 'success');
      loadRuns();
    } catch (err) {
      addToast(err.message || 'Processing failed', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div>
      <div className="admin-panel-head">
        <div>
          <h3><i className="fas fa-hand-holding-usd"></i> Dividend distributions</h3>
          <p className="admin-panel-sub">
            Create a pro-rata rental distribution for all active shareholders of a property.
            Approve, then process in batches (safe for large investor counts — idempotent per recipient).
          </p>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={loadRuns} disabled={loading}>
          <i className={`fas fa-sync${loading ? ' fa-spin' : ''}`}></i> Refresh
        </button>
      </div>

      <form className="admin-form-card" onSubmit={handleCreate} style={{ marginBottom: '1.5rem' }}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dist-property">Property</label>
            <select
              id="dist-property"
              value={form.propertyId}
              onChange={(e) => setForm((f) => ({ ...f, propertyId: e.target.value }))}
              required
            >
              <option value="">Select property</option>
              {saleProperties.map((p) => (
                <option key={p.id} value={p.id}>{p.title || p.id}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="dist-period">Period (e.g. 2026-03)</label>
            <input
              id="dist-period"
              type="text"
              placeholder="2026-03"
              value={form.periodKey}
              onChange={(e) => setForm((f) => ({ ...f, periodKey: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="dist-gross">Gross distributable (USD)</label>
            <input
              id="dist-gross"
              type="number"
              min="0.01"
              step="0.01"
              value={form.grossUsd}
              onChange={(e) => setForm((f) => ({ ...f, grossUsd: e.target.value }))}
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="dist-note">Note (optional)</label>
          <input
            id="dist-note"
            type="text"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="March 2026 rental income"
          />
        </div>
        <button type="submit" className="btn btn-primary btn-sm">Create draft run</button>
      </form>

      <div className="admin-table-wrap">
        <table className="investment-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Period</th>
              <th>Gross</th>
              <th>Recipients</th>
              <th>Status</th>
              <th>Paid</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-light)' }}>No distribution runs yet</td></tr>
            ) : runs.map((run) => (
              <tr key={run.id}>
                <td>{run.propertyTitle || run.propertyId}</td>
                <td>{run.periodKey}</td>
                <td>{formatUsd(run.grossDistributableUsd)}</td>
                <td>{run.totalRecipients ?? '—'}</td>
                <td><span className="status-badge status-pending">{statusLabel(run.status)}</span></td>
                <td>{formatUsd(run.totalPaidUsd || 0)}</td>
                <td style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  {run.status === 'draft' && (
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => handleApprove(run.id)}>
                      Approve
                    </button>
                  )}
                  {(run.status === 'approved' || run.status === 'processing') && (
                    <button
                      type="button"
                      className="btn btn-accent btn-sm"
                      disabled={processingId === run.id}
                      onClick={() => handleProcess(run.id)}
                    >
                      {processingId === run.id ? 'Processing…' : 'Process batch'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDistributionPanel;
